import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { GetMyOrganisationResponse } from '@dreamai/types';
import api from '../api';

/**
 * Whether organisations are switched on for the signed-in user (feature flag
 * `organisations.enabled`, reported as `enabled` on `GET /organisations/me`).
 * Organisation entry points — Settings → Organisation, the onboarding
 * "invite colleagues" prompt — render only when this is `true`. `null` means
 * the answer is not known yet (still loading); logged out or a failed request
 * is `false`. Treat anything but `true` as "not enabled" so nothing
 * organisation-related flashes for a user who does not have it.
 */
export function useOrganisationsEnabled(): boolean | null {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      setEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetMyOrganisationResponse>(
          '/organisations/me',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setEnabled(data.enabled === true);
      } catch (err) {
        console.error('Error checking whether organisations are enabled:', err);
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return enabled;
}
